/**
 * The KnowIt adapter for the two *asking* endpoints W7 introduces:
 * `POST api/ai-actions/taskize` and `POST api/ai-actions/notify`.
 *
 * Wire convention, measured (W7 §B1): **`taskize` answers a bare object whose top-level key
 * happens to be `data`** — not the `{data, policies}` envelope, so there are no policies to
 * read and unwrapping one key is the whole translation. **`notify` answers a flat bare
 * object.** Both ride the 204-tolerant REST lane, which is also where their refusals keep
 * their machine-readable names: `text_required`, `thinker_unavailable`, `parse_failed`,
 * `thinker_failed`. Those names are the UI's only reliable discriminator — a translated
 * sentence is not one — and the thinker's own message is what a person needs to read, so it
 * is carried through verbatim rather than replaced with "Error 502".
 *
 * ⚠ `ai-actions/taskize` performs **no document authorization** server-side (only
 * `[Authorize]`): any signed-in member can post any `documentId` and the server will load its
 * text. That is a server gap (W7 §E2 R2, the same class as `transform-selection`); the client
 * mitigation is to keep every ENTRY POINT gated on `documentUpdate` and never to expose a
 * free-text taskize box. FakeApi enforces that gate so a regression fails a test.
 */
import { AutomationError, SculptError } from "~/api/errors";
import {
  type NotifyInput,
  type NotifyOutcome,
  NotifyOutcomeSchema,
  type TaskizeInput,
  type TaskizeResult,
  TaskizeResultSchema,
} from "~/api/intentTypes";

/** The 204-tolerant bare-JSON lane, the same one the `v2/documents/*` routes ride. */
export interface IntentsTransport {
  rest(path: string, method: string, body?: Record<string, unknown>): Promise<unknown>;
}

/**
 * Keep the wire code, lose the borrowed class name. `rest` raises `SculptError` for any coded
 * refusal because that is the lane's convention; an intent refusal is not a sculpt, so it is
 * re-thrown as the generic coded error the rest of the ops surface already uses.
 */
function recode(error: unknown): never {
  if (error instanceof SculptError) {
    throw new AutomationError(error.code, error.message, error.status);
  }
  throw error;
}

function unwrapData(value: unknown): unknown {
  if (value && typeof value === "object" && !Array.isArray(value) && "data" in value) {
    return (value as { data: unknown }).data;
  }
  return value;
}

export function createKnowItIntents(transport: IntentsTransport) {
  return {
    intents: {
      taskize: async (input: TaskizeInput): Promise<TaskizeResult> => {
        const raw = await transport
          .rest("ai-actions/taskize", "POST", {
            documentId: input.documentId,
            ...(input.collectionId ? { collectionId: input.collectionId } : {}),
            ...(input.documentTitle ? { documentTitle: input.documentTitle } : {}),
            ...(input.selectedText ? { selectedText: input.selectedText } : {}),
            ...(input.thinkerId ? { thinkerId: input.thinkerId } : {}),
          })
          .catch(recode);
        return TaskizeResultSchema.parse(unwrapData(raw) ?? {});
      },

      /**
       * ⚠ Server-side this is **authentication and nothing else** — any member can announce to
       * "everyone with access" (W7 §E2 R3). The Hub's policy service still resolves recipients
       * against the resource ACL at drain, so nobody is told something they may not read; but
       * the BROADCAST is ungated, which is why slate keeps the affordance behind
       * `workspaceUpdate` and the gap is recorded rather than papered over.
       */
      notify: async (input: NotifyInput): Promise<NotifyOutcome> => {
        const raw = await transport
          .rest("ai-actions/notify", "POST", {
            message: input.message,
            ...(input.audienceRole ? { audienceRole: input.audienceRole } : {}),
            ...(input.documentId ? { documentId: input.documentId } : {}),
            ...(input.collectionId ? { collectionId: input.collectionId } : {}),
          })
          .catch(recode);
        return NotifyOutcomeSchema.parse(raw ?? {});
      },
    },
  };
}
